


import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { adminService } from '@/services/adminService';
import { ArrowLeft, LogIn, Loader2, AlertTriangle } from 'lucide-react';

const DEV_BYPASS_ENABLED = import.meta.env.VITE_DEV_ADMIN_BYPASS_ENABLED === 'true';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Countdown timer for rate limiting
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRateLimited && rateLimitCountdown > 0) {
      interval = setInterval(() => {
        setRateLimitCountdown(prev => {
          if (prev <= 1) {
            setIsRateLimited(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRateLimited, rateLimitCountdown]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await adminService.login(email, password);
      
      if (response.success) {
        toast({ 
          title: "Login Successful", 
          description: "Redirecting to admin dashboard..." 
        });
        localStorage.removeItem('devBypassActive'); // Clear bypass if normal login succeeds
        navigate('/admin-dashboard');
      } else {
        toast({ 
          variant: "destructive", 
          title: "Login Failed", 
          description: response.message || "Login failed" 
        });
      }
    } catch (error: any) {
      console.error("Admin login error:", error);
      
      let errorMessage: string;
      let errorTitle: string = "Login Failed";
      
      if (error.message?.includes('Too many requests') || error.message?.includes('rate limit')) {
        errorTitle = "Rate Limited";
        errorMessage = "Too many login attempts. Please wait 15 minutes before trying again.";
        setIsRateLimited(true);
        setRateLimitCountdown(900); // 15 minutes in seconds
      } else if (error.message?.includes('Invalid email or password') || error.message?.includes('Not authorized as admin')) {
        errorMessage = "Invalid email or password or not authorized as admin.";
      } else if (error.message?.includes('Network') || error.message?.includes('connection')) {
        errorMessage = "Network error. Please check your internet connection and ensure the backend is running.";
      } else if (error.response?.status === 429) {
        errorTitle = "Rate Limited";
        errorMessage = "Too many login attempts. Please wait 15 minutes before trying again.";
        setIsRateLimited(true);
        setRateLimitCountdown(900); // 15 minutes in seconds
      } else {
        errorMessage = error.message || "Login failed. Please try again.";
      }
      
      toast({ 
        variant: "destructive", 
        title: errorTitle,
        description: errorMessage, 
        duration: 8000 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBypassLogin = () => {
    if (isMounted && DEV_BYPASS_ENABLED) {
      localStorage.setItem('devBypassActive', 'true');
      toast({ title: "Dev Bypass Activated", description: "Redirecting to admin dashboard...", variant: "default" });
      navigate('/admin-dashboard');
    } else {
      toast({ variant: "destructive", title: "Error", description: "Bypass is not enabled or component not mounted."});
    }
  };

  const handleResetRateLimit = () => {
    setIsRateLimited(false);
    setRateLimitCountdown(0);
    toast({ title: "Rate Limit Reset", description: "Rate limit has been cleared for development.", variant: "default" });
  };

  return (
    <div className="flex flex-col min-h-screen items-center justify-center bg-main-bg text-foreground p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold text-primary">Admin Login</CardTitle>
          <CardDescription>Access the vTech Navigator Admin Dashboard.</CardDescription>
        </CardHeader>
        <CardContent>
          {isRateLimited && (
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-center text-yellow-600">
                <AlertTriangle className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">
                  Rate limited. Please wait {Math.floor(rateLimitCountdown / 60)}:{(rateLimitCountdown % 60).toString().padStart(2, '0')} before trying again.
                </span>
              </div>
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-input border-gray-600 text-foreground focus:border-accent focus:ring-accent"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-input border-gray-600 text-foreground focus:border-accent focus:ring-accent"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-3 text-base" 
              disabled={isLoading || isRateLimited}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : isRateLimited ? (
                <AlertTriangle className="mr-2 h-5 w-5" />
              ) : (
                <LogIn className="mr-2 h-5 w-5" />
              )}
              {isLoading ? 'Logging in...' : isRateLimited ? `Rate Limited (${Math.floor(rateLimitCountdown / 60)}:${(rateLimitCountdown % 60).toString().padStart(2, '0')})` : 'Login'}
            </Button>
          </form>

          {isMounted && DEV_BYPASS_ENABLED && (
            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground mb-2 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 mr-1 text-yellow-500" />
                Development bypass enabled.
              </p>
              <Button
                variant="outline"
                onClick={handleBypassLogin}
                disabled={isLoading}
                className="w-full border-yellow-500 text-yellow-600 hover:bg-yellow-500/10"
              >
                Bypass Login (Dev Only)
              </Button>
            </div>
          )}

          <div className="mt-4 text-center">
            <p className="text-xs text-muted-foreground">
              Rate limit: 30 login attempts per 15 minutes
            </p>
            {isMounted && DEV_BYPASS_ENABLED && isRateLimited && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetRateLimit}
                className="mt-2 text-xs text-blue-500 hover:text-blue-600"
              >
                Reset Rate Limit (Dev)
              </Button>
            )}
          </div>

        </CardContent>
      </Card>
      <Button variant="outline" asChild className="mt-8">
        <Link to="/" className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
      </Button>
    </div>
  );
}
